import Head from "next/head"
import Link from "next/link"
import dynamic from "next/dynamic"
import { useRouter } from "next/router"
import { BlitzPage, Routes } from "@blitzjs/next"
import Layout from "app/core/layouts/Layout"
import Footer from "components/footer"
import { PlayIcon, PauseIcon } from "@radix-ui/react-icons"
import { useEffect, useRef } from "react"
import {
  TES_NATIONAL_EXPLORER_CONTACT_LINK,
  PARTNER_WITH_AF_LINK,
  TAKE_ACTION_LINK,
  AF_CAREER_RESOURCES_LINK,
  TES_NATIONAL_EXPLORER_STORIES_LINK,
  TES_NATIONAL_EXPLORER_RESOURCES_LINK,
} from "app/constants"
import { clearMapPositionSessionStorage } from "hooks/use_map"
import { STATIC_ASSETS_CLOUDFRONT_URL } from "app/constants"
import { useVideo } from "hooks/use_video"
import { useTranslation } from "react-i18next"
import { PRVIReleaseBanner } from "app/features/dashboard/components/release-banner"
// Dynamic import to avoid SSR and hydration errors
const Header = dynamic(() => import("components/header"), { ssr: false })

function Benefit({ src, children }: { src: string; children: React.ReactNode }) {
  return (
    <div className="m-auto text-center">
      <img alt="" src={src} className="m-auto max-h-10 max-w-10" />
      <div className="pt-2 text-xs font-bold uppercase leading-tight tracking-wider text-gray-700">
        {children}
      </div>
    </div>
  )
}

const featured = [
  {
    image: "/icons/ne-ln.png",
    name: "National Explorer",
    desc: "Explore our flagship national map. Find your score today and help create Tree Equity.",
    link: "/map",
  },
  {
    image: "/icons/tesa-ln.png",
    name: "Local Analyzers",
    desc: "Learn about the Tree Equity Score Analyzer, our local, deep-dive application.",
    link: "/analyzer",
  },
  {
    image: "/icons/resources-ln.png",
    name: "Starter Guide",
    desc: "Take the next steps. Explore our step-by-step guides, watch a demo, and browse resources.",
    link: TES_NATIONAL_EXPLORER_RESOURCES_LINK,
  },
  {
    image: "/icons/stories-ln.png",
    name: "Stories",
    desc: "Delve into Tree Equity storytelling, maps and data visualizations.",
    link: TES_NATIONAL_EXPLORER_STORIES_LINK,
  },
] as const

const action = [
  {
    image:
      "https://links.imagerelay.com/cdn/3910/ql/a8148930e3d440e3aff3ac218eb52660/woman-with-megaphone.jpg",
    name: "Take action today",
    link: TAKE_ACTION_LINK,
  },
  {
    image: "/detroit_cokko_swain_sm.jpg",
    name: "Partner with us",
    link: PARTNER_WITH_AF_LINK,
  },
  {
    image:
      "https://links.imagerelay.com/cdn/3910/ql/6d344b56862f47f9921f9c13d6462aa6/ForestryPhoto--30-of-99-.jpg",
    name: "Forestry careers",
    link: AF_CAREER_RESOURCES_LINK,
  },
  {
    image:
      "https://links.imagerelay.com/cdn/3910/ql/d57a1cfd8cc04bcd95d0fd2414d1a76c/Weyerhaeuser-Ruston-LA-planting-60.JPG.jpg.JPG",
    name: "Contact us",
    link: TES_NATIONAL_EXPLORER_CONTACT_LINK,
  },
] as const

/*
 * https://unsplash.com/photos/MTeZ5FmCGCU
 *
 * https://unsplash.com/photos/UTuWr_s0LrM
 */
function TesHome() {
  const pathname = typeof window !== "undefined" && window.location.pathname
  const video = useRef<HTMLVideoElement>(null)
  const { toggleVideoPlay, isPlaying } = useVideo(video)
  const router = useRouter()
  const { i18n } = useTranslation()
  useEffect(() => {
    if (pathname && pathname !== "/") {
      router.replace(pathname) // Redirect to the right page...
    }
  }, [pathname])

  if (pathname && pathname !== "/") return null

  useEffect(() => {
    if (i18n?.language !== "en") {
      i18n?.changeLanguage("en")
    }
  }, [i18n])

  return (
    <div>
      <Head>
        <meta
          name="description"
          content="Find your score and help create Tree Equity in cities and towns across America."
        />
        <meta
          property="og:image"
          content={`${STATIC_ASSETS_CLOUDFRONT_URL}/hero_video_still.jpg`}
          key="image"
        />
      </Head>
      <div className="bg-white">
        <Header />
      </div>
      <PRVIReleaseBanner lng="en" />
      <section className="relative h-[78rem] md:h-[60rem] lg:h-[66rem]">
        <video
          id="hero-video"
          className="top-100 absolute left-0 z-[-1] hidden aspect-[1280/720] w-full object-cover md:block md:h-3/5 lg:h-5/6"
          loop
          autoPlay
          playsInline
          muted
          ref={video}
          src="https://d17m5nraxo9zm6.cloudfront.net/hero-video.webm"
          poster="https://d17m5nraxo9zm6.cloudfront.net/hero_video_still.jpg"
        ></video>
        <button
          onClick={toggleVideoPlay}
          className="absolute right-4 top-4 z-10 hidden h-6 w-6 items-center justify-center rounded-full border border-white text-white md:flex"
        >
          {!isPlaying && <PlayIcon />}
          {isPlaying && <PauseIcon />}
        </button>
        <img
          src="https://d17m5nraxo9zm6.cloudfront.net/hero_video_still.jpg"
          className="top-100 absolute left-0 z-[-1] block h-5/6 w-full object-cover md:hidden"
        />
        <div className="m-auto h-[100vh] w-11/12 pt-[10%] sm:pt-[6%] md:pt-[5%] lg:pt-[7%] xl:w-2/3">
          <h1 className="w-full text-[32px] font-bold leading-[38px] text-white [text-shadow:_0_1px_10px_rgb(0_0_0_/_80%)] md:text-[46px] md:leading-[50px] lg:w-3/5 lg:text-[52px] lg:leading-[58px] xl:w-3/5">
            Help create Tree Equity in cities and towns across America.
          </h1>
          <div className="pt-6">
            <button
              onClick={() => {
                clearMapPositionSessionStorage()
                router.push(Routes.NationalExplorer())
              }}
            >
              <a
                target="blank"
                rel="noreferrer noopener"
                className="inline-block rounded-full bg-brand-green-dark px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-brand-green sm:text-base"
              >
                Find your Tree Equity Score
              </a>
            </button>
          </div>
          <div
            style={{
              backgroundImage: "url(/Parcel_Tree_Outlines_noROW_lite_blue.jpg)",
              backgroundSize: "cover",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
            }}
            className="mx-auto mt-52 pl-4 sm:pl-8 md:mt-36 lg:mt-40 xl:mt-48"
          >
            <div className="m-auto w-11/12 font-medium text-gray-700">
              <h2 className="pb-4 pr-20 pt-12 text-3xl sm:pr-0 sm:text-4xl lg:text-[40px] lg:leading-tight xl:text-[45px]">
                Discover <span className="text-brand-green-dark">Tree Equity Score</span>
              </h2>
              <p className="font-base pb-12 pr-2 text-sm sm:pr-8 sm:leading-normal md:text-lg md:font-semibold">
                Trees are critical urban infrastructure that are essential to public health and
                well-being. Tree Equity Score was created to help address damaging environmental
                inequities by prioritizing human-centered investment in areas with the greatest
                need.
              </p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 pb-16 md:grid-cols-4">
                {featured.map((item, i) => {
                  if (item.link === "/map")
                    return (
                      <a
                        className="cursor-pointer border-b-[3px] border-transparent pb-2 transition-all duration-200 hover:border-brand-green-dark"
                        key={i}
                        onClick={() => {
                          clearMapPositionSessionStorage()
                          router.push(Routes.NationalExplorer())
                        }}
                      >
                        <img alt="" src={item.image} className="h-14" />
                        <div>
                          <div className="pt-2 text-base font-bold lg:text-lg">{item.name}</div>
                          <div className="pt-2 text-sm lg:text-base">{item.desc}</div>
                        </div>
                      </a>
                    )
                  else
                    return (
                      <Link
                        key={i}
                        href={`${item.link}`}
                        className="border-b-[3px] border-transparent pb-2 transition-all duration-200 hover:border-brand-green-dark"
                      >
                        <img alt="" src={item.image} className="h-14" />
                        <div>
                          <div className="pt-2 text-base font-bold lg:text-lg">{item.name}</div>
                          <div className="pt-2 text-sm lg:text-base">{item.desc}</div>
                        </div>
                      </Link>
                    )
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
      <div id="documentary" className="h-20"></div>
      <section className="bg-gradient-to-b from-[#222222] to-black">
        <img
          src={`${STATIC_ASSETS_CLOUDFRONT_URL}/campaign/stripe-left.svg`}
          className="hidden h-32 md:block"
        ></img>
        <div className="grid grid-cols-1 lg:grid-cols-3 lg:gap-4">
          <div className="m-auto mt-0 w-11/12 pb-16 pt-10 sm:w-4/5 md:-mt-12 md:pt-0 lg:col-span-2">
            <iframe
              className=""
              height="560"
              width="315"
              src="https://www.youtube-nocookie.com/embed/nOJtEMevtkQ"
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          </div>
          <div className="m-auto w-2/3 lg:m-0">
            <img
              src={`${STATIC_ASSETS_CLOUDFRONT_URL}/campaign/mapping_path_logo_white_colorpop.png`}
              width="300px"
            />
            <p className="pt-6 text-base font-normal leading-relaxed text-white xl:text-lg">
              In "Mapping the Path to Tree Equity," a short documentary by American Forests, follow
              Leandro's journey as he leads a community-driven movement in Providence, RI and uses
              Tree Equity Score to address unequal tree distribution.
            </p>
            <p className="h-16"></p>
          </div>
        </div>

        <img
          src={`${STATIC_ASSETS_CLOUDFRONT_URL}/campaign/stripe-right.svg`}
          className="float-right -mt-[114px] hidden h-28 md:block"
        ></img>
      </section>
      <div className="bg-gradient-to-b from-white to-[#ECF5F2] font-medium text-gray-700">
        <div className="h-40"></div>
        <div className="m-auto w-3/5">
          <h2 className="text-center text-2xl text-brand-green-dark sm:text-4xl">
            Change powered by science
          </h2>
          <p className="pt-4 text-center text-lg sm:text-xl">
            Anyone can use Tree Equity Score to chart a course of action. Join the movement to
            advance Tree Equity in urban communities nationwide and contribute to creating greener,
            safer and more resilient cities.
          </p>
        </div>
      </div>
      <div className="bg-[#ECF5F2] font-medium text-gray-700">
        <div className="h-32 lg:h-10"></div>
        <div className="m-auto w-11/12 md:w-5/6 lg:w-11/12 xl:w-5/6">
          <div className="grid items-center lg:flex">
            <img alt="" loading="lazy" src="/NE_Laptop.png" className="m-auto lg:w-3/5" />
            <div>
              <p className="pl-[5%] pt-5 text-base leading-normal md:pr-[5%] md:pt-0 lg:pl-0">
                Tree Equity Score measures how well the critical benefits of urban tree canopy are
                reaching those who need them most. The score establishes an equity-first standard to
                guide investment in communities living on low incomes, communities of color and all
                those disproportionately affected by extreme heat, pollution and other environmental
                hazards.
              </p>
              <h2 className="py-4 pl-[5%] text-base font-bold md:pr-[5%] lg:pl-0">
                Tree Equity Score combines information from a variety of sources to create a single
                measure from 0 to 100. The lower the score, the greater the need for investment.
              </h2>
              <div className="m-auto grid w-4/5 grid-cols-2 gap-x-2 gap-y-5 px-4 py-4 sm:grid-cols-4 lg:m-0 lg:w-full xl:w-11/12">
                <Benefit src="/icons/tree.svg">Tree canopy</Benefit>
                <Benefit src="/icons/buildingdensity.svg">Building density</Benefit>
                <Benefit src="/icons/income.svg">Income & Employment</Benefit>
                <Benefit src="/icons/race.svg">Race</Benefit>
                <Benefit src="/icons/temp.svg">Surface temperature</Benefit>
                <Benefit src="/icons/health.svg">Health</Benefit>
                <Benefit src="/icons/language.svg">Language</Benefit>
                <Benefit src="/icons/age.svg">Age</Benefit>
              </div>
            </div>
          </div>
        </div>
        <div className="h-24"></div>
      </div>
      <section
        className="bg-[#ECF5F2]"
        style={{
          backgroundImage: "url(/Parcel_Tree_Outlines_noROW_lite_grey_thin.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
          imageRendering: "crisp-edges",
        }}
      >
        <div className="h-32"></div>
        <div className="text-center">
          <p className="pb-2 font-bold uppercase tracking-wide text-brand-green-dark">
            Meet the apps
          </p>
          <p className="text-2xl font-medium text-gray-700 sm:text-4xl">
            Our Tree Equity Score Products
          </p>
        </div>

        <div className="h-20"></div>
        <div className="m-auto w-11/12 xl:w-4/5 2xl:w-2/3">
          <div className="grid md:grid-cols-2">
            <div className="content-center bg-white p-8 shadow-[0px_0px_20px_rgba(0,0,0,0.1)] sm:mb-10">
              <h3 className="pb-4 text-center text-base font-medium uppercase text-gray-700 md:text-lg">
                Tree Equity Score
                <span className="font-bold text-brand-green-dark"> National Explorer</span>
              </h3>
              <img alt="" loading="lazy" src="/AppsComp_NE.png" />
              <div className="pt-4 text-left text-sm font-medium text-gray-700 lg:text-base">
                Our flagship tool makes Tree Equity Score available to all urban areas in the United
                States. Tree Equity Score sets a national standard to help make the case for
                investment in areas with the greatest need.
                <ul className="list-disc pl-10 pt-4">
                  <li>Find your score among all urban neighborhoods in the United States.</li>
                  <li>
                    Locate data to inform and support equity-first tree planting and investment.
                  </li>
                  <li>
                    Explore municipal and regional targets and communicate the benefit to the
                    community.
                  </li>
                </ul>
              </div>
              <button
                onClick={() => {
                  clearMapPositionSessionStorage()
                  router.push(Routes.NationalExplorer())
                }}
              >
                <a className="mb-8 mt-6 inline-block rounded-full bg-brand-green px-5 py-2.5 text-sm font-semibold uppercase tracking-wider text-white transition hover:bg-brand-green-dark">
                  Start Exploring
                </a>
              </button>
            </div>
            <div className="content-center bg-brand-green px-8 pt-8 shadow-[0px_0px_20px_rgba(0,0,0,0.2)] sm:mt-10">
              <h3 className="pb-4 text-center text-base font-medium uppercase text-white md:text-lg">
                Tree Equity Score <span className="font-bold">Analyzers</span>
              </h3>
              <img alt="" loading="lazy" src="/AppsComp_TESA.png" />
              <div className="pt-4 text-left text-sm font-medium text-white lg:text-base">
                Take a deep dive into Tree Equity in your community. Tree Equity Score Analyzers
                have single-city or region coverage and support customizable property-level planting
                plans.
                <ul className="list-disc pl-10 pt-4">
                  <li>
                    Make a data-driven plan to shift neighborhood Tree Equity Scores in your city or
                    region.
                  </li>
                  <li>
                    Customize Tree Equity Score targets, then create tree planting and tree
                    protection plans at the property level to achieve your goals.
                  </li>
                  <li>Track your progress and measure the positive impacts to your community.</li>
                </ul>
              </div>
              <Link
                href="/analyzer"
                className="mb-16 mt-6 inline-block rounded-full bg-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider text-brand-green-dark transition hover:bg-brand-green-dark hover:text-white sm:mb-8"
              >
                Learn more
              </Link>
            </div>
          </div>
        </div>
        <div className="h-32"></div>
      </section>
      <div className="bg-[#005251] px-8 py-16 text-white md:px-0">
        <div className="m-auto w-11/12 md:w-4/5">
          <p className="pb-4 text-center text-2xl font-bold sm:text-3xl lg:mx-auto lg:px-16">
            Join the movement to protect and restore urban forests
          </p>
          <p className="text-center text-base lg:mx-auto lg:px-40 lg:text-lg">
            Urban communities require trees as essential infrastructure to support public health and
            climate resilience. According to our estimations, we need to plant and grow 500 million
            trees and protect existing urban tree infrastructure to achieve Tree Equity across
            America.
          </p>
        </div>
        <div className="m-auto w-11/12 pt-8 text-brand-green-dark sm:w-2/3 md:w-4/5 lg:w-11/12 xl:w-4/5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {action.map((item, i) => {
              return (
                <Link key={i} href={`${item.link}`} target="_blank" rel="noreferrer noopener">
                  <div className="m-auto max-w-[350px] bg-white transition duration-200 hover:scale-[1.01] hover:shadow-[0px_0px_20px_rgba(0,0,0,0.25)]">
                    <div className="">
                      <p className="ml-3 inline text-lg font-bold leading-[50px]">{item.name}</p>
                      <img
                        alt=""
                        src="/icons/actions-right-arrow.png"
                        className="float-right mr-2 inline w-6 py-3"
                      />
                    </div>
                    <div className="">
                      <img alt="" src={item.image} className="h-[240px] object-cover" />
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}

const Home: BlitzPage = () => {
  return <TesHome />
}

Home.suppressFirstRenderFlicker = true
Home.getLayout = (page) => <Layout>{page}</Layout>
export default Home
